import { Box, Typography } from "@mui/joy";
import {
  LayoutDashboard,
  Layers,
  FileStack,
  Store,
  Menu,
  Plus,
} from "lucide-react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { Gate } from "../../shared";
import { PERMISSIONS } from "../../constants/permissions";

interface BottomNavProps {
  onOpenMenu?: () => void;
}

export default function BottomNav({ onOpenMenu }: BottomNavProps) {
  const location = useLocation();

  const items = [
    { name: "Home", href: "/dashboard", icon: LayoutDashboard },
    { name: "Projects", href: "/project", icon: Layers },
    { name: "Reports", href: "/reports/list", icon: FileStack },
    { name: "Suppliers", href: "/suppliers", icon: Store },
  ];

  return (
    <Box
      component="nav"
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        display: { xs: "flex", sm: "none" },
        alignItems: "stretch",
        minHeight: 56,
        bgcolor: "background.surface",
        borderTop: "1px solid",
        borderColor: "divider",
        zIndex: 1100,
        pb: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onOpenMenu}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
          border: "none",
          borderRight: "1px solid",
          borderColor: "divider",
          bgcolor: "transparent",
          color: "text.secondary",
          cursor: "pointer",
          py: 0.75,
        }}
      >
        <Menu size={20} />
        <Typography level="body-xs" sx={{ fontSize: "10px", fontWeight: 500 }}>
          Menu
        </Typography>
      </Box>

      {items.map((item) => {
        const isActive = location.pathname.startsWith(item.href);
        const Icon = item.icon;
        return (
          <NavLink
            key={item.href}
            to={item.href}
            style={{
              flex: 1,
              textDecoration: "none",
              color: "inherit",
              borderRight: "1px solid var(--standard-palette-divider)",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={0.25}
              sx={{
                height: "100%",
                minHeight: 56,
                color: isActive ? "primary.solidBg" : "text.secondary",
                bgcolor: isActive ? "primary.softBg" : "transparent",
                borderTop: "2px solid",
                borderTopColor: isActive ? "primary.solidBg" : "transparent",
                py: 0.75,
              }}
            >
              <Icon size={20} strokeWidth={isActive ? 2.25 : 2} />
              <Typography
                level="body-xs"
                sx={{ fontSize: "10px", fontWeight: isActive ? 700 : 500 }}
              >
                {item.name}
              </Typography>
            </Box>
          </NavLink>
        );
      })}

      <Gate anyOf={[PERMISSIONS.PROJECT_CREATE]}>
        <Box
          component={Link}
          to="/project/new-quick"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.25,
            textDecoration: "none",
            color: "primary.solidBg",
            bgcolor: "primary.softBg",
            py: 0.75,
          }}
        >
          <Plus size={22} strokeWidth={2.25} />
          <Typography
            level="body-xs"
            sx={{ fontSize: "10px", fontWeight: 700 }}
          >
            New
          </Typography>
        </Box>
      </Gate>
    </Box>
  );
}
